import { Paper } from "@material-ui/core";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { getJobCategoryOptions } from "../../constants";
import { t } from "../../i18n/util";
import { ApplicationStateEnum } from "../../network/APITypes";
import { THEME } from "../../theme/ThemeStore";
import { HrBotNotificationType } from "../../types/models";
import { pushRoute } from "../app/router/history";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { HrRoutes } from "../hr/router/HrRoutes";
import { downloadSecureLink, optionToString } from "../util/helpers";
import { ApplicantPicture, HrBotButton, NoApplicantPicture } from "./Primitives";
import { StyledDoneIcon } from "./SideBar";

type Props = {
    notification: HrBotNotificationType;
    setOpenDialog: React.Dispatch<
        React.SetStateAction<
            | {
                  startTime: Date;
                  endTime: Date;
                  id: string;
                  applicantId: string;
                  applicationStatus: ApplicationStateEnum;
              }
            | undefined
        >
    >;
    closeNotifications: () => void;
};

export const HrBotNotification = observer(({ notification, setOpenDialog, closeNotifications }: Props) => {
    const active = notification.createdAt > notification.lastRead;
    const isMobile = useMobileMediaQuery();

    const isCurrentAppointmentConfirmation =
        (notification.applicant.applicationState === "first_interview" &&
            notification.type === "appointments_selected_first_interview") ||
        (notification.applicant.applicationState === "second_interview" &&
            notification.type === "appointments_selected_second_interview");

    const confirmedAppointments =
        (notification.type === "appointments_selected_first_interview" ||
            notification.type === "appointments_selected_second_interview") &&
        notification.appointments &&
        notification.appointments.some((appointment) => appointment.confirmed);

    const header = (
        <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
            onClick={() => {
                pushRoute(HrRoutes.APPLICANT.DETAILS, {
                    params: { applicantId: notification.applicant.id }
                });
                closeNotifications();
            }}
            className="caption"
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: THEME.customColors.headingsDark,
                    wordBreak: "break-word"
                }}
                className="body2semibold"
            >
                {active && (
                    <div
                        style={{
                            backgroundColor: THEME.customColors.primary,
                            borderRadius: "50%",
                            height: 12,
                            width: 12
                        }}
                    />
                )}
                {notification.applicant.photoUrl ? (
                    <ApplicantPicture
                        style={{ height: 36, width: 36, backgroundImage: `url(${notification.applicant.photoUrl})` }}
                    />
                ) : (
                    <NoApplicantPicture style={{ height: 36, width: 36, fontSize: 12, minWidth: 36 }}>
                        {notification.applicant.firstName[0] ?? ""}
                        {notification.applicant.lastName[0] ?? ""}
                        {notification.applicant.lastName[1] ?? ""}
                    </NoApplicantPicture>
                )}
                {`${notification.applicant.firstName} ${notification.applicant.lastName}`}
            </div>
            {moment().to(notification.createdAt)}
        </div>
    );

    const applicationContent = (
        <p className="body2" style={{ color: THEME.customColors.body1Dark }}>
            <FormattedMessage
                id="screen.dashboard.appliedFor"
                values={{
                    jobCategory: optionToString(getJobCategoryOptions(), notification.applicant.jobCategory),
                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                }}
            />
        </p>
    );

    const returnFromEvidenceContent = (
        <p className="body2" style={{ color: THEME.customColors.body1Dark }}>
            {t("screen.dashboard.returnFromEvidence")}
        </p>
    );

    const codeExampleContent = notification.submittedFile ? (
        <div
            className="body2"
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 16,
                color: THEME.customColors.body1Dark,
                // needed to prevent stretching of codeFile paper
                alignItems: "flex-start"
            }}
        >
            {t("screen.dashboard.codingSubmissionResponse")}
            {notification.submittedFile !== null && (
                <Paper
                    style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        gap: 16,
                        cursor: "pointer"
                    }}
                    className="blackDalBorder codeExampleFile"
                    onClick={() => {
                        downloadSecureLink(notification.submittedFile?.url, notification.submittedFile?.name);
                    }}
                >
                    <img src={THEME.Images.icFileBlack} alt="" />
                    <div>
                        <div
                            style={{
                                color: THEME.customColors.headingsDark,
                                wordBreak: "break-word",
                                maxWidth: 200
                            }}
                            className="body2semibold"
                        >
                            {notification.submittedFile.name}
                        </div>
                        <div className="caption">
                            {Math.ceil((notification.submittedFile.size as number) / 104857.6) / 10}{" "}
                            {t("screen.dataUpload.mb")}
                        </div>
                    </div>
                </Paper>
            )}
        </div>
    ) : (
        <></>
    );

    const dateContent = notification.appointments ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p className="body2" style={{ color: THEME.customColors.body1Dark }}>
                {isCurrentAppointmentConfirmation && (
                    <>
                        {notification.appointments.length === 1 && (
                            <FormattedMessage
                                id="screen.dashboard.dateConfirmation"
                                values={{
                                    title: notification.appointments[0].title,
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        )}
                        {notification.appointments.length > 1 && (
                            <FormattedMessage
                                id="screen.dashboard.multipleDateConfirmation"
                                values={{
                                    title: notification.appointments[0].title,
                                    b: (chunks: Array<React.ReactElement | string>) => <b>{chunks}</b>
                                }}
                            />
                        )}
                    </>
                )}
                {!isCurrentAppointmentConfirmation && t("screen.dashboard.dateConfirmation.oldAppointment")}
            </p>
            {isCurrentAppointmentConfirmation &&
                notification.appointments.map((appointment) => (
                    <div
                        style={{
                            display: "flex",
                            gap: 16,
                            alignItems: "center",
                            justifyContent: "space-between",
                            height: 45
                        }}
                        key={appointment.id}
                    >
                        <p className="body2" style={{ color: THEME.customColors.body1Dark }}>
                            {moment(appointment.startTime).format(t("screen.dateTimeFormat"))} -{" "}
                            {moment(appointment.endTime).format(t("screen.timeFormat"))}
                        </p>
                        {!confirmedAppointments && (
                            <HrBotButton
                                label={t("screen.dashboard.confirm")}
                                onClick={() => {
                                    setOpenDialog({
                                        startTime: appointment.startTime,
                                        endTime: appointment.endTime,
                                        id: appointment.id,
                                        applicantId: notification.applicant.id,
                                        applicationStatus: notification.applicant.applicationState
                                    });
                                }}
                                style={{ fontSize: 12, padding: "12px 24px", width: "auto" }}
                            />
                        )}
                        {confirmedAppointments && appointment.confirmed && (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    color: THEME.customColors.body1Dark,
                                    gap: 8
                                }}
                                className="body2"
                            >
                                <StyledDoneIcon />
                                {t("screen.appointmentConfirmation.confirmed")}
                            </div>
                        )}
                    </div>
                ))}
        </div>
    ) : (
        <p className="body2" style={{ color: THEME.customColors.body1Dark }}>
            {t("screen.dashboard.deletedDateConfirmation")}
        </p>
    );

    return (
        <div
            className="notification"
            style={{
                margin: "0 20px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                paddingBottom: isMobile ? 16 : 8
            }}
        >
            {header}
            {notification.type === "application_received" && applicationContent}
            {notification.type === "coding_submission_received" && codeExampleContent}
            {notification.type === "appointments_selected_first_interview" && dateContent}
            {notification.type === "appointments_selected_second_interview" && dateContent}
            {notification.type === "application_from_evidence" && returnFromEvidenceContent}
        </div>
    );
});
